import dayjs from 'dayjs';
import { camelize, camelizeKeys } from 'humps';
import { computed } from 'vue';

import { useMetricFormatting } from '@/app/dashboards/composables/useMetricFormattingSetup';
import { useReport } from '@/app/dashboards/composables/useReport';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import {
  CONTENT_REPORT_DEFAULT_MEDIA_METRICS,
  CONTENT_REPORT_MEDIA_LIST_SIZES,
  CUSTOM_METRICS_PREFIX,
  SENTIMENTS,
} from '@/app/dashboards/constants';
import { getGoalChipProps } from '@/app/dashboards/utils/report.utils';
import { METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import { sourceTypeEnum } from '@/app/library/constants';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { formatPostDataToMediaCardProps } from '@/app/socialListening/utils/formatters';
import { constants, MEDIA_CARD_FORMAT, SOURCE_TO_METRIC_PROP } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { DATA_TYPES, POST_TYPES } from '@/models/dashboards/metrics.constants';
import { useIdentityStore } from '@/stores/identity';
import { useMetricsStore } from '@/stores/metrics';
import { getMediaVideoDuration, isMediaVideo, resolvePlatformMediaType } from '@/utils/media';
import { getUserTimezone } from '@/utils/timezone';

export function useContentReportDetails({ report, loading }) {
  const metricsStore = useMetricsStore();
  const identityStore = useIdentityStore();
  const {
    firstMetric,
    isCustomMetricReportTile,
    isStoryFrameReport,
    isStoryGalleryReport,
    currency,
    reportBrands,
    reportMeta,
    reportChannel,
    reportCompetitors,
    reportData,
    reportMetric,
    reportMetricDetails,
    reportCategory,
    showGoalChip,
    thumbnailMode,
    visionAiReport,
  } = useReport({
    report,
    loading,
  });

  const { isListeningReportCategory } = useReportCategory({ reportCategory });

  const { getCustomReportDetailsFromId } = useCustomMetrics();

  const customMetricDetails = computed(() => {
    if (!isCustomMetricReportTile.value) return null;

    return getCustomReportDetailsFromId(firstMetric.value);
  });

  const contentReportParentMetric = computed(() => {
    if (isCustomMetricReportTile.value) return customMetricDetails?.value?.name;

    return reportMetricDetails.value?.parentMetric ?? reportMetric.value;
  });

  const contentReportMetricsMap = computed(() => {
    if (isStoryFrameReport.value) {
      return CONTENT_REPORT_DEFAULT_MEDIA_METRICS[reportChannel.value].storyFrameMetrics.map(
        (metric) => metric,
      );
    }
    if (reportChannel.value === CHANNELS.FACEBOOK.value)
      return ['ENGAGEMENT_RATE_V2', 'VIEWS', 'TOTAL_ENGAGEMENTS_V2'];
    return CONTENT_REPORT_DEFAULT_MEDIA_METRICS[reportChannel.value]?.metrics.map(
      (metric) => metric,
    );
  });
  const allContentMetricDetails = computed(() => {
    const channelMetricDetails =
      metricsStore.metricDetails?.[reportChannel.value]?.[METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT];
    if (!channelMetricDetails) return [];
    return Object.entries(channelMetricDetails).map(([key, detail]) => ({
      ...camelizeKeys(detail),
      metricKey: key,
    }));
  });

  const maxNumberOfMedia = computed(() => {
    if (visionAiReport.value) {
      return thumbnailMode.value
        ? CONTENT_REPORT_MEDIA_LIST_SIZES.VISION_AI_THUMBNAIL_MODE
        : CONTENT_REPORT_MEDIA_LIST_SIZES.VISION_AI_EXPANDED_MODE;
    }
    return CONTENT_REPORT_MEDIA_LIST_SIZES.DASHBOARDS;
  });

  const contentList = computed(() => {
    const content = reportData.value?.content ?? [];
    return content.slice(0, maxNumberOfMedia.value);
  });

  const highlightedMetricDetails = computed(() =>
    allContentMetricDetails.value.find(
      (metricDetail) => metricDetail?.metricKey === contentReportParentMetric?.value,
    ),
  );

  const { getFormatConfig } = useMetricFormatting({
    dataType: highlightedMetricDetails.value?.dataType,
    isCustomMetricReportTile,
    customMetricDetails,
    currency,
  });

  const metricsToDisplayOnCard = computed(() => {
    if (!contentReportMetricsMap.value) return [];
    const metricsFromMap = allContentMetricDetails.value.filter((metricDetail) =>
      contentReportMetricsMap.value.includes(metricDetail?.metricKey),
    );
    if (isCustomMetricReportTile.value) return metricsFromMap.concat([customMetricDetails.value]);

    const highlightedMetric = highlightedMetricDetails.value;
    if (!highlightedMetric) return metricsFromMap;

    const hasDuplicateDisplayName = metricsFromMap.some(
      (metric) => metric?.displayName === highlightedMetric.displayName,
    );

    if (hasDuplicateDisplayName) {
      return metricsFromMap.map((metric) =>
        metric?.displayName === highlightedMetric.displayName ? highlightedMetric : metric,
      );
    }

    return metricsFromMap.concat([highlightedMetric]);
  });

  function getSourceData(media, sourceKey) {
    if (sourceKey === constants.INSTAGRAM.toLowerCase()) {
      return {
        ...media,
        ...media[sourceKey],
        likeshop_clicks: media?.likeshop?.clicks,
      };
    }
    return { ...media, ...media[sourceKey] };
  }

  function getMediaSrc(mediaItem) {
    const mediaImages = mediaItem?.video ? mediaItem?.video?.thumbnails : mediaItem?.image?.sizes;

    if (!mediaImages) {
      return mediaItem.thumb;
    }
    return mediaImages.smallSquare?.url || '';
  }

  function getMediaCardFormatData(dataType) {
    switch (dataType) {
      case DATA_TYPES.FLOAT:
        return MEDIA_CARD_FORMAT.TWO_DECIMAL;
      case DATA_TYPES.PERCENTAGE:
        return MEDIA_CARD_FORMAT.PERCENTAGE;
      case DATA_TYPES.SECONDS:
        return MEDIA_CARD_FORMAT.DURATION;
      case DATA_TYPES.MILLISECONDS:
        return MEDIA_CARD_FORMAT.DURATION_MILLISECONDS;
      default:
        return MEDIA_CARD_FORMAT.NUMBER;
    }
  }

  function formatMediaItemMetric(mediaItem, metricDetail) {
    if (metricDetail?.key?.startsWith(CUSTOM_METRICS_PREFIX)) {
      const customMetricId = Number(metricDetail.key.replace(CUSTOM_METRICS_PREFIX, ''));
      const targetMetric =
        mediaItem.customMetrics.find((customMetric) => customMetric.id === customMetricId) ?? {};
      const value = targetMetric?.value;
      const format = getMediaCardFormatData(metricDetail?.originalMetricFormat);

      return {
        value,
        highlight: contentReportParentMetric.value === metricDetail?.name,
        label: metricDetail?.name,
        sourceKey: metricDetail?.name,
        postType: [],
        ...format,
      };
    }

    const sourceDataKey = camelize(metricDetail?.sourceDataName ?? '');
    const value = mediaItem?.[sourceDataKey];
    const highlight = contentReportParentMetric.value === metricDetail?.metricKey;
    const format = getMediaCardFormatData(metricDetail?.dataType);
    let postType = metricDetail?.postTypes?.[0];
    if (
      metricDetail?.postTypes?.includes(POST_TYPES.ORGANIC) &&
      metricDetail?.postTypes?.includes(POST_TYPES.PROMOTED)
    )
      postType = POST_TYPES.ORGANIC_PROMOTED;

    return {
      value,
      highlight,
      label: metricDetail?.name ?? metricDetail?.displayName,
      sourceKey: metricDetail?.name ?? metricDetail?.sourceDataName,
      postType,
      percentOfGoal: mediaItem.percentOfGoal,
      ...format,
    };
  }

  const reportBrandsById = computed(() =>
    reportBrands.value.reduce((acc, brand) => {
      acc[brand.id] = brand;
      return acc;
    }, {}),
  );

  const reportCompetitorsById = computed(() =>
    reportCompetitors.value.reduce((acc, competitor) => {
      acc[competitor.id] = competitor;
      return acc;
    }, {}),
  );

  function getAccount(sourceData) {
    switch (sourceData.sourceType) {
      case sourceTypeEnum.UGC:
        return {
          avatar: sourceData?.creator?.avatarUrl,
          name: sourceData?.creator?.handle,
        };
      case sourceTypeEnum.COMPETITIVE:
        return reportCompetitorsById.value?.[sourceData.sourceAccountId];
      default:
        return reportBrandsById.value?.[sourceData.brandId];
    }
  }

  const canAccessSentiment = computed(() =>
    identityStore.checkIfBrandPermissionsExist(
      [BRAND.GENERAL.CAN_ACCESS_SENTIMENT],
      reportBrands.value,
    ),
  );

  function includeSentiment(sourceKey, sourceType) {
    const disallowedPlatforms = [
      constants.INSTAGRAM_STORY_FRAME,
      constants.LINKEDIN,
      constants.PINTEREST,
      constants.THREADS,
    ];
    const sentimentSourceTypes = [sourceTypeEnum.OWNED, sourceTypeEnum.UGC];
    const isTikTokUGCSource =
      sourceKey === constants.TIKTOK.toLowerCase() && sourceType === sourceTypeEnum.UGC;
    const hasADisallowedPlatformAndSource =
      disallowedPlatforms.some((platform) => platform.toLowerCase() === sourceKey) ||
      isTikTokUGCSource;
    const hasAPermittedSourceType = sentimentSourceTypes.includes(sourceType);

    return canAccessSentiment.value && hasAPermittedSourceType && !hasADisallowedPlatformAndSource;
  }

  function addGoalChip(mediaResult, sortedMetric) {
    if (!sortedMetric) return;
    mediaResult.goalChip = getGoalChipProps(
      reportMeta.value.goals?.overall,
      sortedMetric.percentOfGoal,
      reportMetricDetails.value.displayName,
      sortedMetric.value,
      getFormatConfig({ value: sortedMetric.value }),
      highlightedMetricDetails.value?.isNegativeMetric,
    );
  }

  function getMediaTypeOptions(media) {
    const camelMedia = camelizeKeys(media);
    const platformMediaType = resolvePlatformMediaType(media);
    const duration = isMediaVideo(camelMedia) ? getMediaVideoDuration(camelMedia) : undefined;

    return {
      platformMediaType: platformMediaType?.toLowerCase(),
      ...(duration != null && { videoDuration: duration }),
    };
  }

  function formatMediaItems(mediaItems) {
    return mediaItems.map((media) => {
      const sourceKey =
        SOURCE_TO_METRIC_PROP[media.source] || (media.source?.toLowerCase() ?? null);
      const sourceData = camelizeKeys({ ...getSourceData(media, sourceKey) });
      const metricItems = metricsToDisplayOnCard.value.map((metricDetail) =>
        formatMediaItemMetric(sourceData, metricDetail),
      );
      const commentSentiment = Object.keys(sourceData?.commentSentiment ?? {}).find(
        (key) => sourceData?.commentSentiment[key],
      );
      const storyFrameImages = media?.latest_media ?? [];
      const formattedStoryFramesImages = storyFrameImages.map((image) => ({
        src: image.image_url,
      }));

      const account = getAccount(sourceData);
      const avatar = { image: account?.avatar, alt: account?.name };

      const publishDateField = sourceData.sourceCreatedAt || sourceData.latestMediaPublishedAt;
      const localPublishDate = dayjs.utc(publishDateField).tz(getUserTimezone()).toDate();
      const sentiment = includeSentiment(sourceKey, sourceData?.sourceType)
        ? SENTIMENTS[commentSentiment]
        : undefined;

      let sortedMetric = metricItems.find((metricItem) => metricItem.highlight);
      let mediaResult = {
        id: sourceData.id,
        brandId: sourceData.brandId,
        brandLabel: reportBrandsById.value?.[sourceData.brandId]?.label,
        src: getMediaSrc(sourceData),
        source_type: sourceData?.sourceType,
        title: account?.name,
        text: sourceData[sourceKey]?.text,
        channel: sourceKey,
        timeZone: getUserTimezone(),
        postDate: localPublishDate,
        sentiment,
        caption: sourceData[sourceKey]?.caption,
        avatar,
        metricItems,
        useStoryFrameCard: isStoryGalleryReport.value,
        storyFramesImages: isStoryGalleryReport.value ? formattedStoryFramesImages : [],
        ...getMediaTypeOptions(media),
      };
      if (isListeningReportCategory.value) {
        mediaResult = { ...mediaResult, ...formatPostDataToMediaCardProps(sourceData) };
        // Currently only engagements supported for goals on SL content reports
        sortedMetric = mediaResult.metricItems.find(
          (item) =>
            item.label === 'Engagements' && contentReportParentMetric.value === 'ENGAGEMENTS',
        );
        if (sortedMetric) sortedMetric.percentOfGoal = sourceData?.percentOfGoal;
      }
      if (showGoalChip.value) {
        addGoalChip(mediaResult, sortedMetric);
      }

      return mediaResult;
    });
  }

  const formattedMediaItems = computed(() => formatMediaItems(contentList.value));

  return {
    formatMediaItems,
    includeSentiment,
    formattedMediaItems,
  };
}
